<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-pv-main">
    <div class="pv-options">
      <div class="pv-filters-and-table-box-controls">
        <div class="pv-filter-summary-container">
          <span>{{ pvFilterSummary }}</span>
        </div>
        <ButtonFilter buttonText="Change Filter" filterPosition="right" :filterWidth="420" :isOpen.sync="pvFilterOpen">
          <div class="button-filter-content-container">
            <BoxContainer title="Payment Date" :isCheckBox="true" :isChecked.sync="filterByPaymentDate">
              <DateTimePicker placeholder="Start Payment Date " :value.sync="paymentStartDate" :showTime="false" :isRequired="filterByPaymentDate"
               :errorMessage.sync="paymentStartDateErr" :isDisabled="!filterByPaymentDate"></DateTimePicker>
              <DateTimePicker placeholder="End Payment Date " :value.sync="paymentEndDate" :showTime="false" :isRequired="filterByPaymentDate"
               :errorMessage.sync="paymentEndDateErr" :isDisabled="!filterByPaymentDate"></DateTimePicker>
            </BoxContainer>
            <BoxContainer title="Payee" :isCheckBox="true" :isChecked.sync="filterByPayee">
              <CheckBoxV2 title="All Payee Type" :isChecked.sync="allPayeeTypeChecked" :isDisabled="!filterByPayee" />
              <InputQuery style="margin-top: 5px;" ref="payeetype" placeholder="Payee Type" :isDisabled="!filterByPayee || allPayeeTypeChecked" :items="payeeTypeList" itemKey="Party"
               :showDesc="false" :selectedObject.sync="selectedPayeeType" :errorMessage.sync="payeeTypeErr" :isRequired="filterByPayee && !allPayeeTypeChecked || allPayeeCodeChecked" />
              <CheckBoxV2 title="All Payee Code" :isChecked.sync="allPayeeCodeChecked" :isDisabled="!filterByPayee || allPayeeTypeChecked" />
              <InputQuery style="margin-top: 5px;" ref="payeecode" placeholder="Payee Code" :items="payeeCodeList" :itemKey="payeeCodeKey" :showDesc="false" :selectedObject.sync="selectedPayeeCode"
                :errorMessage.sync="payeeCodeErr" :isRequired="filterByPayee && !allPayeeTypeChecked && !allPayeeCodeChecked" :isDisabled="!filterByPayee || allPayeeTypeChecked || allPayeeCodeChecked || selectedPayeeType == null" />
            </BoxContainer>
            <BoxContainer title="Payment No" :isCheckBox="true" :isChecked.sync="filterByPayNo">
              <InputQuery ref="startpayno" placeholder="Start Payment No." :isRequired="filterByPayNo" :showDesc="false" :errorMessage.sync="startPaymentNoErr" :isDisabled="!filterByPayNo"
               :selectedObject.sync="selectedStartPaymentNo" :search.sync="startPayNoSearch" :items="paymentNoList" itemKey="Payment No" />
              <InputQuery ref="endpayno" placeholder="End Payment No" :isRequired="filterByPayNo" :showDesc="false" :errorMessage.sync="endPaymentNoErr" :isDisabled="!filterByPayNo"
               :selectedObject.sync="selectedEndPaymentNo" :search.sync="endPayNoSearch" :items="paymentNoList" itemKey="Payment No" />
            </BoxContainer>
            <div class="refresh-button-container">
              <button @click="refreshClicked">Refresh</button>
            </div>
          </div>
        </ButtonFilter>
        <button class="select-deselect-all-button" :disabled="selectDeselectAllDisabled" @click="selectDeselectAllClicked">
          {{ selectDeselectAllButtonText }}
        </button>
      </div>
      <div class="pv-tablebox-container">
        <TableBox title="Payment Voucher" :tableData.sync="paymentTableData" :selectedRow.sync="selectedPV" :maxSelection="paymentTableData.length" :dataType="pvDataType"
          :loading="pvLoading" :hideColumns="hiddenCols" />
      </div>
      <div style="margin-top: 10px;">
        <div v-if="verifyByField">
          <InputQuery placeholder="Verify By" :showDesc="false" :items="verifyByList" :selectedObject.sync="selectedVerify" itemKey="Employee ID" />
        </div>
        <div class="header-formatting-controls">
          <CheckBoxV2 v-if="pageFormatOption" :title="pageFormatCaption" :isChecked.sync="pageFormatChecked" />
          <CheckBoxV2 title="Show Company Letter Head" :isChecked.sync="showLetterHeadChecked" />
          <CheckBoxV2 v-if="groupByDebitAccOption" title="Group By Debit Account" :isChecked.sync="groupByDebitChecked" />
          <Dropdown style="width: 50%; margin-top: 10px;" placeholder="Currency Format" :items="currencyFormatItems" itemKey="name" :selectedObject.sync="selectedCurrencyFormat" :isRequired="true" :errorMessage.sync="currencyFormatErrorMessage" />
        </div>
      </div>
      <div class="call-to-action-buttons">
        <button @click="previewPVClicked">Preview Payment Voucher</button>
      </div>
    </div>

    <div class="pv-preview">
      <DesignerV3 v-if="showDesignerV3" :reportFiles="designerV3ReportFiles" :reportData="designerV3ReportData" :sharedData="designerV3SharedData" :canToggleBackground="false"
       :canShare="true" :shareBehaviour="designerV3ShareBehaviour" :canClose="false" @shareClicked="designerV3ShareClicked" @afterPrint="designerV3AfterPrint" ref="designerV3" />
      <div v-else class="user-instructions">
        <span>Select some payment voucher and click on <strong>Preview Payment Voucher</strong> to get started</span>
      </div>
    </div>

    <PopupV2 class="email-pv-popup" :show="showEmailPVPopup" icon="https://dsmlib.logistics.systems/Icons/BlackMail.png" :buttons="emailPVPopupButtons"
      title="Email Payment Voucher" width="60%" @buttonClicked="emailPVPopupButtonClicked">
      <div class="email-pv-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPVPopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPVPopupCCMails" title="CC" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailPVPopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailPVPopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailPVPopupMessageText" :maxLength="1000" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  components: {
  },
  computed: {
    selectDeselectAllDisabled: function() {
      return this.paymentTableData.length == 0;
    },

    selectDeselectAllButtonText: function() {
      if (this.paymentTableData.length == 0) {
        return "Select All";
      }
      return this.paymentTableData.length != this.selectedPV.length ? "Select All" : "Deselect All";
    },
  },
  data: function() {
    return {
      mountedPromisesFulfilledCount: -1,
      userDetails: null,

      pvFilterSummary: "",
      pvFilterOpen: false,

      filterByPaymentDate: true,
      paymentStartDate: new Date(), paymentStartDateErr: "",
      paymentEndDate: new Date(), paymentEndDateErr: "",

      filterByPayee: false,
      allPayeeTypeChecked: false,
      allPayeeCodeChecked: false,
      payeeTypeList: [],
      selectedPayeeType: null, payeeTypeErr: "",
      payeeCodeList: [],
      payeeCodeKey: "",
      payeeCodeDescKey: "",
      selectedPayeeCode: null, payeeCodeErr: "",

      filterByPayNo: false,
      paymentNoList: [],
      selectedStartPaymentNo: null, startPaymentNoErr: "",
      startPayNoSearch: "",
      selectedEndPaymentNo: null, endPaymentNoErr: "",
      endPayNoSearch: "",

      hiddenCols: ['#', 'Payee Type', 'Payee', 'Job No', 'Indicator', 'LHDN Status', 'Authorized', 'Cheque No', 'Bank', 'P & C', 'Doc Ref No', 'Remark'],
      paymentTableData: [],
      selectedPV: [],
      pvDataType: {
        'Payment No': 'string',
        'Payment Date': 'date',
        'Payee Type': 'string',
        'Payee Code': 'string',
        'Total': 'currency',
        'Outstanding': 'currency'
      },
      pvLoading: false,

      verifyByField: false,
      verifyByList: [],
      selectedVerify: null,

      showLetterHeadChecked: true,

      pageFormatOption: false,
      pageFormatCaption: "",
      pageFormatChecked: false,

      groupByDebitAccOption: false,
      groupByDebitChecked: false,

      currencyFormatItems: [
        { name: "Original Currency", APIValue: "original" },
        { name: "Local Currency", APIValue: "MYR" }, // =============== hardcoded for now
      ],
      selectedCurrencyFormat: null, currencyFormatErrorMessage: "",
      chequeSign: false,

      //#region DesignerV3
      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: [],
      designerV3ShareBehaviour: {
        behaviour: "uploadAndEmit",
        options: {
          fileName: "",
          folder: ""
        }
      },
      //#endregion

      //#region email
      showEmailPVPopup: false,
      emailPVPopupButtons: [
        {"text": "Send Email"},
        {"text": "Cancel", "color": "lightgray"},
      ],
      emailPVPopupToMails: [],
      emailPVPopupCCMails: [],
      emailPVPopupBCCMails: [],
      emailPVPopupSubjectInputValue: "",
      emailPVPopupMessageText: "",
      //#endregion
    };
  },
  props: {
  },
  methods: {
    updateMountedLoaderMessage: function(totalPromises) {
      this.mountedPromisesFulfilledCount++;
      const completionPercentage = ((this.mountedPromisesFulfilledCount / totalPromises) * 100).toFixed(0);
      this.$fullscreenLoading(`Getting Data ${completionPercentage}%`);
    },

    refreshClicked: async function(pointerEvent) {
      let error = false;

      if (this.filterByPaymentDate) {
        if (!this.paymentStartDate) {
          this.paymentStartDateErr = 'Start Payment Date is required!';
          error = true;
        }
        
        if (!this.paymentEndDate) {
          this.paymentEndDateErr = 'End Payment Date is required!';
          error = true;
        }
      }

      if (this.filterByPayNo) {
        if (!this.selectedStartPaymentNo && !this.startPayNoSearch.trim()) {
          this.startPaymentNoErr = 'Start Payment No. is required!';
          error = true;
        }

        if (!this.selectedEndPaymentNo && !this.endPayNoSearch.trim()) {
          this.endPaymentNoErr = 'End Payment No. is required!';
          error = true;
        }
      }

      if (this.filterByPayee) {
        if (!this.allPayeeTypeChecked && !this.selectedPayeeType) {
          this.payeeTypeErr = 'Payee Type is required!';
          error = true;
        }
        
        if (!this.allPayeeTypeChecked && !this.allPayeeCodeChecked && !this.selectedPayeeCode) {
          this.payeeCodeErr = 'Payee Code is required!';
          error = true;
        }
      }

      if (error) return;

      if (!this.filterByPaymentDate && !this.filterByPayNo) {
        const confirm = await this.$showAlert("warning", "This May Take A While", "You are not filtering by Payment Date and Payment No. \n\nLoading this data may take a while. Proceed?",
          [{text: "Proceed"}, {text: "Cancel", color: "lightgray"}]
        );

        if (confirm === "Cancel") return;
      }

      this.pvFilterOpen = false;
      this.updatePVFilterSummary();
      await this.getFilteredPVList();
    },

    getFilteredPVList: async function() {
      let requestBody = {
        'startdate': this.filterByPaymentDate ? this.paymentStartDate.formatString('yyyy-MM-dd') : "",
        'enddate': this.filterByPaymentDate ? this.paymentEndDate.formatString('yyyy-MM-dd') : "",
        'startpaymentno': this.filterByPayNo ? (this.selectedStartPaymentNo ? this.selectedStartPaymentNo['Payment No'] : this.startPayNoSearch) : "",
        'endpaymentno': this.filterByPayNo ? (this.selectedEndPaymentNo ? this.selectedEndPaymentNo['Payment No'] : this.endPayNoSearch) : "",
        'allpayeetype': this.filterByPayee && this.allPayeeTypeChecked ? '1' : '0',
        'payeetype': this.filterByPayee && !this.allPayeeTypeChecked ? this.selectedPayeeType['Party Code'] : "",
        'allpayeecode': this.filterByPayee && !this.allPayeeTypeChecked && this.allPayeeCodeChecked ? '1' : '0',
        'payeecode': this.filterByPayee && !this.allPayeeTypeChecked && !this.allPayeeCodeChecked ? this.selectedPayeeCode[this.payeeCodeKey] : "",
      };

      this.paymentTableData = [];
      this.selectedPV = [];
      this.pvLoading = true;

      let paymentListing = await this.$apiCallV2('/PrintPaymentVoucher/GetPaymentListing', requestBody); // chg API if not reusing GetPaymentListing
      this.paymentTableData = paymentListing.filter((p) => p['Indicator'] != 'C');
      if (this.paymentTableData.length > 0 && this.paymentTableData[0]['Payment No'] == "string") { 
        this.paymentTableData.splice(0,1);
        if (this.paymentTableData.length === 1) {
          this.$nextTick(() => {
            this.selectedPV = [...this.paymentTableData];
          })
        }
      }
      this.pvLoading = false;
    },

    updatePVFilterSummary: function() {
      let filtersList = [];

      if (this.filterByPaymentDate) {
        filtersList.push(`date from ${this.paymentStartDate.formatString('yyyy-MM-dd')} to ${this.paymentEndDate.formatString('yyyy-MM-dd')}`);
      }

      if (this.filterByPayNo) {
        filtersList.push(
          `Payment No. from ${this.selectedStartPaymentNo ? this.selectedStartPaymentNo['Payment No'] : this.startPayNoSearch} to 
          ${this.selectedEndPaymentNo ? this.selectedEndPaymentNo['Payment No'] : this.endPayNoSearch}`
        );
      }

      if (this.filterByPayee) {
        let filterPayee = "";
        if (this.allPayeeTypeChecked) {
          filterPayee = 'All Payee Type';
        } else {
          if (this.allPayeeCodeChecked) {
            filterPayee = `All ${this.selectedPayeeType['Party']}`;
          } else {
            filterPayee = `Payee Type ${this.selectedPayeeType['Party']}, Payee Code: ${this.selectedPayeeCode[this.payeeCodeKey]}`
          }
        } 
        filtersList.push(filterPayee);
      }

      this.pvFilterSummary = `Filter by ${filtersList.join(', ')}`;
    },
    
    selectDeselectAllClicked: function(pointerEvent) {
      if (this.paymentTableData.length !== this.selectedPV.length) {
        this.selectedPV = [];
        this.$nextTick(() => {
          this.selectedPV = [...this.paymentTableData];
        });
      } else {
        this.selectedPV = [];
      }
    },

    previewPVClicked: async function(pointerEvent) {
      this.currencyFormatErrorMessage = "";
      this.pvFilterOpen = false;
      if (this.selectedPV.length == 0) {
        this.$showAlert("error", 'No Payment Voucher Selected', "Please select at least one payment voucher to preview", [{text: "OK"}]);
        return;
      }

      if (!this.selectedCurrencyFormat) {
        this.currencyFormatErrorMessage = "Currency Format required!";
        return;
      }

      const requestBody = {
        pvarray: this.selectedPV.map((pv) => pv['Payment No']).join(','),
        showcompanyletterhead: this.showLetterHeadChecked ? '1' : '0',
        groupbydebitacc: this.groupByDebitAccOption && this.groupByDebitChecked ? '1' : '0',
        // verifyby: this.verifyByField && this.selectedVerify ? this.selectedVerify['Employee ID'] : "", //TBC
        company: this.userDetails.CoName,
        coshortname: this.userDetails.CoRefname,
        currency: this.selectedCurrencyFormat['APIValue']
      };

      this.$fullscreenLoading("Getting Payment Voucher Details");
      let pvDetails = await this.$apiCallV2("/PrintPaymentVoucher/PrintPV", requestBody);
      if (Array.isArray(pvDetails) && pvDetails[0].Results === '1') {
        this.$showAlert("error", "Error Printing Payment Voucher", pvDetails[0].ErrMsg, [{text: "OK"}]);
        this.$fullscreenLoading();
        return;
      }
      pvDetails = pvDetails['Payment'];

      const coName = this.userDetails.CoRefname;
      pvDetails.General['Show Company Letter Head'] = pvDetails.General['Show Company Letter Head'] !== '0';
      pvDetails.General['companyheader'] = this.userDetails['CoHeaderFin'] ? this.userDetails['CoHeaderFin'] : 
      pvDetails.General['_signatureRequired'] = true;
      pvDetails.General['chequeSignatories'] = this.chequeSign || coName.startsWith("FM") || coName == 'FIMA'; //temp put coName condition, remove later

      this.$fullscreenLoading("Preparing to Print");
      for (let pv of pvDetails.Payments) {
        if (coName == 'TNLS') {
          pv.pvTitle = `${pv.paymenttype == 'CH' ? 'CHEQUE' : (pv.paymenttype == 'CS' ? 'CASH' : '')} PAYMENT VOUCHER`;
        } else {
          pv.pvTitle = "PAYMENT VOUCHER";
        }

        switch (coName) { //switch case for header
          case 'MAXF':
          case 'MAXL':
            const addr1 = pv['Addr1'] ?? "";
            const addr2 = pv['Addr2'] ?? "";
            const addr3 = pv['Addr3'] ?? "";
            let addr4 = (`${pv['Addr4']}<br>` ?? "") + `${pv.postcode} ${pv.city}<br>${pv.state} ${pv.country}`;
            if (pv.phone) {addr4 += `${pv.phone} `}
            if (pv.fax) {addr4 += pv.fax}
            if (pv.contact) {addr4 += `<br>Attn: ${pv.contact}`}
            
            pv._pvHeader1 = [ 
              {
                leftLabel: 'Pay To',
                colon: ':',
                leftValue: `<b>${pv.Payee}</b>`,
                rightLabel: 'Ref No',
                rcolon: ':',
                rightValue: `<b>${pv['Payment No']}</b>`
              },
              {
                leftLabel: "",
                colon: "",
                leftValue: addr1,
                rightLabel: 'Date',
                rcolon: ':',
                rightValue: `<b>${pv.Date}</b>`
              },
              {
                leftLabel: "",
                colon: "",
                leftValue: addr2,
                rightLabel: "Job No",
                rcolon: ':',
                rightValue: `<b>${pv['Job No'] ? pv['Job No'] : ''}</b>`
              },
              {
                leftLabel: "",
                colon: "",
                leftValue: addr3,
                rightLabel: "Prepared by",
                rcolon: ':',
                rightValue: pv['UserID']
              },
              {
                leftLabel: "",
                colon: "",
                leftValue: addr4,
                rightLabel: "Page",
                rcolon: ':',
                rightValue: "Page {{jsreportPageNum}} of {{jsreportTotalPage}}"
              },
              {
                leftLabel: "Customer",
                colon: ":",
                leftValue: pv.Details.cu_name ?? "",
                rightLabel: pv.DocRefNo,
                rcolon: ':',
                rightValue: pv['Chq No'] ? `<b><u>${pv['Chq No']}</u></b>` : ""
              },
              {
                leftLabel: "",
                colon: "",
                leftValue: "",
                rightLabel: pv.DocBank,
                rcolon: pv.DocBank && pv.DocBank.trim() != '' ? ':' : '',
                rightValue: pv['Bank Code'] ? `<b>${pv['Bank Code']}</b>` : ""
              }
            ]

            pv._pvHeader2 = [
              {
                leftLabel: "Paid the sum of",
                colon: ":",
                leftValue: `<u><b>${pv['Total Amt Word']}</u></b>`
              }
            ]

            pv._paymentDetailsInTable = true;
            pv.remarksValue = pv.Details.Remark;
            pv._jobRemarks = false;
            pv.preparedByTop = true;
            break;

          default:
            pv._pvHeader1 = [ 
              {
                leftLabel: 'Pay To',
                colon: ':',
                leftValue: coName == 'TNLS' ? `${pv.PayeeCode}` : `<u><b>${pv.Payee}</b></u>`,
                rightLabel: coName.startsWith('FM') ? 'No.' : 'Ref #',
                rcolon: ':',
                rightValue: `<b>${pv['Payment No']}</b>`
              },
              {
                leftLabel: ['FIMA', 'TNLS'].includes(coName)  ? '' : 'Paid the sum of',
                colon: ':',
                leftValue: coName == 'FIMA' ? '' : (coName == 'TNLS' ? `<u><b>${pv.Payee}</b></u>` : `<u><b>${pv['Total Amt Word']}</u></b>`),
                rightLabel: 'Date',
                rcolon: ':',
                rightValue: `<b>${pv.Date}</b>`
              },
              {
                leftLabel: "",
                colon: "",
                leftValue: "",
                rightLabel: coName.startsWith('FM') ? 'Job No' : 'Job #',
                rcolon: ':',
                rightValue: `<b>${pv['Job No'] ? pv['Job No'] : ''}</b>`
              },
              {
                leftLabel: "",
                colon: "",
                leftValue: "",
                rightLabel: pv.DocRefNo,
                rcolon: ':',
                rightValue: pv['Chq No'] ? `<b><u>${pv['Chq No']}</u></b>` : ""
              },
              {
                leftLabel: "",
                colon: "",
                leftValue: "",
                rightLabel: pv.DocBank,
                rcolon: pv.DocBank && pv.DocBank.trim() != '' ? ':' : '',
                rightValue: pv['Bank Code'] ? `<b>${pv['Bank Code']}</b>` : ""
              }
            ]

            pv._jobRemarks = pv.Details._jobRemarks == 'true' ? [{remarksLabel: "Remarks", remarksValue: pv.Details.Remark}] : false;

            if (pv.Details._jobHeader1 == 'true' && !coName.includes("TS")) {
              switch (coName) { // switch case for job header
                case "FMH":
                case "FMG": 
                case "FMT": 
                case "FMP":
                case "FMGTEST":
                  pv._jobHeader1 = [
                    {
                      leftLabel: pv.Details.Vsl_Title ?? "Vessel / Voy No.",
                      leftValue: pv.Details.vessel ?? "",
                      rightLabel: "",
                      rightValue: "",
                    },
                    {
                      leftLabel: pv.Details.eta ?? "ETA",
                      leftValue: pv.Details.sdate ?? "",
                      rightLabel: "",
                      rightValue: ""
                    },
                    {
                      leftLabel: pv.Details.Bl_Title ?? "B/L No.",
                      leftValue: pv.Details.BLNo ?? "",
                      rightLabel: "",
                      rightValue: "",
                    },
                    {
                      leftLabel: pv.Details.Customer ?? "Shipper",
                      leftValue: pv.Details.Consignee ?? "",
                      rightLabel: "",
                      rightValue: "",
                    },
                    {
                      leftLabel: pv.Details.Shipper ?? "Consignee",
                      leftValue: pv.Details.ShipperName ?? "",
                      rightLabel: "",
                      rightValue: "",
                    }
                  ]
                  break;

                default:
                  pv._jobHeader1 = [
                    {
                      leftLabel: pv.Details.Vsl_Title ?? "Vessel / Voy No.",
                      leftValue: pv.Details.vessel ?? "",
                      rightLabel: "Bkg Ref #",
                      rightValue: pv.Details.BookingRefNo ? `: ${pv.Details.BookingRefNo}` : "",
                    },
                    {
                      leftLabel: pv.Details.eta ?? "ETA",
                      leftValue: pv.Details.sdate ?? "",
                      rightLabel: "Origin/Dest",
                      rightValue: pv.Details.Port ? `: ${pv.Details.Port}` : "",
                    },
                    {
                      leftLabel: pv.Details.Bl_Title ?? "B/L No.",
                      leftValue: pv.Details.BLNo ?? "",
                      rightLabel: "Shipper",
                      rightValue: pv.Details.Consignor ? `: ${pv.Details.Consignor}` : "",
                    },
                    {
                      leftLabel: "GWeight/Meas",
                      leftValue: `${pv.Details.GWeight ?? ""} KG ${pv.Details.Measurement ?? ""} M3`,
                      rightLabel: "Consignee",
                      rightValue: pv.Details.Consignee ? `: ${pv.Details.Consignee}` : "",
                    },
                  ]
                  break;
              }
            } else if (pv.Details._jobHeader1 == 'false' && coName == 'TNLS') {
              pv._jobHeader1 = [
                {
                  leftLabel: pv.Details["Ship / Con Title"],
                  leftValue: pv.Details.Consignee ?? "",
                  rightLabel: "",
                  rightValue: ""
                }
              ]
            }
            break;
        }

        const jobCharges = pv.Details.Charges ? this.formatJobCharges(pv.Details.Charges) : [];
        const apInv = pv.Details.AP ? this.formatAPInv(pv.Details.AP) : [];
        const dp = pv.Details.GL ? this.formatDP(pv.Details.GL) : [];
        let temp = jobCharges.concat(apInv, dp);
        pv.transactions = temp.map((t, index) => { return {
          seq: index + 1,
          ...t
        }})

        switch (coName) {
          case 'DEMOTS':
          case 'TS':
            pv.docnoLabel = "Doc No";
            pv.docdateLabel = "Doc Date";
            pv.descLabel = "Description";
            pv.jobnoLabel = "Job No";
            pv.taxcodeLabel = "Currency";
            pv.amtexcltaxLabel = "F. Amount";
            pv.taxamtLabel = "Exch. Rate";
            pv.amtincltaxLabel = `Amount<br>(${pvDetails.General.selectedcurr == 'original' ? pv.fcurrency : pv.Currency})`;
            pv.preparedBy = `${pv.Details.UserCreate} ${pv.Details.DateCreate}`;
            pv.printedBy = pvDetails.General.preparedby;
            break;

          default:
            pv.docnoLabel = "Doc No";
            pv.docdateLabel = "Doc Date";
            pv.descLabel = "Description";
            pv.jobnoLabel = "Job No";
            pv.taxcodeLabel = "Tax Code";
            pv.amtexcltaxLabel = (coName.startsWith("FM") || coName.startsWith("MAX") ? 'Org. Amt' : 'Amt. Excl. Tax') + `<br>(${pvDetails.General.selectedcurr == 'original' ? pv.fcurrency : pv.Currency})` 
            pv.taxamtLabel = `Tax Amt.<br>(${pvDetails.General.selectedcurr == 'original' ? pv.fcurrency : pv.Currency})`;
            pv.amtincltaxLabel = (coName.startsWith("FM") || coName.startsWith("MAX") ? 'Paid Amt.' : 'Amount') + `<br>(${pvDetails.General.selectedcurr == 'original' ? pv.fcurrency : pv.Currency})`;
            pv.preparedBy = pvDetails.General.preparedby;
            break;
        }
        pv.TotalAmtInWord = ['TNLS', 'FIMA'].includes(coName) ? (coName == 'FIMA' ? "Paid the sum of: " : "" ) + pv['Total Amt Word'] : "";
        pv.tabularInvAmtDetail = coName.startsWith("MAX");
        pv.showPrintedBy = coName.includes("TS");
        pv.oricurr = pv.fcurrency;
        pv.localcurr = pv.fcurrency == pv.Currency ? "" : pv.Currency;
        pv.grandTotalInclTax = pv.fcurrency == pv.Currency ? "" : this.formatNumber(pv.Total);
        pv.grandFTotalInclTax = pv.FTotal ? this.formatNumber(pv.FTotal) : '0.00';

        if (pv.QRURL) {
          pv.QRImage = await this.$getQRURL(pv.QRURL, {size:150, margin:10});
        }
      }
      this.$fullscreenLoading();

      this.designerV3ReportFiles = Array.from({length: pvDetails.Payments.length}, () => "PaymentVoucher.json");
      this.designerV3ReportData = pvDetails.Payments;
      this.designerV3SharedData = pvDetails.General;
      this.showDesignerV3 = true;

    },

    formatJobCharges: function(charges) {
      let allJobCharges = [];
      for (const chrg of charges) {
        let detail = {};
        const desc = chrg.Description ? (chrg.Remark ? `${chrg.Description}<br><i>${chrg.Remark}</i>` : chrg.Description) : (chrg.Remark ? `<i>${chrg.Remark}</i>` : "");
        switch (this.userDetails.CoRefname) {
          case 'DEMOTS':
          case "TS":
            detail = {
              docNo: chrg.ChargesCode ?? "",
              docDate: '',
              description: desc,
              jobNo: chrg.JobNo || chrg.RefNo ? `${chrg.JobNo ?? ""} ${chrg.RefNo ?? ""}` : "",
              taxCode: chrg.currency ?? "", //currency for TS
              amtExclTax: chrg['foffsetamt'] ? this.formatNumber(chrg['foffsetamt']) : '0.00', //FAmount for TS
              taxAmt: chrg.rate ? this.formatNumber(chrg['rate']) : '1.00', //Exch rate for TS
              amtInclTax: chrg['OffsetAmt'] ? this.formatNumber(chrg['OffsetAmt']) : '0.00',
            }
            break;

          default:
            detail = {
              docNo: chrg.ChargesCode ?? "",
              docDate: '',
              description: desc,
              jobNo: chrg.JobNo || chrg.RefNo ? `${chrg.JobNo ?? ""} ${chrg.RefNo ?? ""}` : "",
              taxCode: chrg.GST2TaxCode ?? "", 
              amtExclTax: chrg['Org.Amt'] ? this.formatNumber(chrg['Org.Amt']) : '0.00', 
              taxAmt: chrg['Tax Amt'] ? this.formatNumber(chrg['Tax Amt']) : '0.00', 
              amtInclTax: chrg['OffsetAmt'] ? this.formatNumber(chrg['OffsetAmt']) : '0.00',
            }
            break;
        }
        allJobCharges.push(detail);
      }
      return allJobCharges;
    },

    formatAPInv: function(apinv) {
      let allAPInv = [];
      for (const inv of apinv) {
        let detail = {};
        switch (this.userDetails.CoRefname) {
          case 'DEMOTS':
          case "TS":
            detail = {
              docNo: inv['inv_no'] ?? "",
              docDate: inv['inv_date'] ?? "",
              description: "",
              jobNo: "",
              taxCode: inv['currency'] ?? "", //currency
              amtExclTax: inv['foffsetamt'] ? this.formatNumber(inv['foffsetamt']) : '0.00', //f.amt
              taxAmt: inv['rate'] ? this.formatNumber(inv['rate']) : '1.00', //exch rate
              amtInclTax: inv['OffsetAmt'] ? this.formatNumber(inv['OffsetAmt']) : '0.00',
            }
            break;

          default:
            const desc = inv['inv_no'] ? (inv.Remark ? `${inv['inv_no']}<br><i>${inv.Remark}</i>` : inv['inv_no']) : (inv.Remark ? `<i>${inv.Remark}</i>` : "");
            detail = {
              docNo: inv['APInv_no'] ?? "",
              docDate: inv['inv_date'] ?? "",
              description: desc,
              jobNo: inv.RefNo ?? "",
              taxCode: inv.GST2TaxCode ?? "",
              amtExclTax: inv['Total_amt'] ? this.formatNumber(inv['Total_amt']) : '0.00',
              taxAmt: inv['Tax Amt'] ? this.formatNumber(inv['Tax Amt']) : '0.00',
              amtInclTax: inv['OffsetAmt'] ? this.formatNumber(inv['OffsetAmt']) : '0.00',
            }
            break;
        }
        allAPInv.push(detail);
      }
      return allAPInv;
    },
    
    formatDP: function(directpost) {
      let allDP = [];
      for (const dp of directpost) {
        let detail = {};
        switch (this.userDetails.CoRefname) {
          case "TS":
          case "DEMOTS":
            detail = {
              docNo: dp['Debit_AC'] ?? "",
              docDate: "",
              description: dp['accdescr'] ?? "",
              jobNo: "", 
              taxCode: dp['currency'] ?? "",
              amtExclTax: dp['FAmount'] ? this.formatNumber(dp['FAmount']) : '0.00',
              taxAmt: dp['Rate'] ? this.formatNumber(dp['Rate']) : '1.00',
              amtInclTax: dp['Amount'] ? this.formatNumber(dp['Amount']) : '0.00',
            }
            break;

          default:
            const desc = dp['accdescr'] ? (dp.Remark ? `${dp['accdescr']}<br><i>${dp.Remark}</i>` : dp['accdescr']) : (dp.Remark ? `<i>${dp.Remark}</i>` : "");
            detail = {
              docNo: (this.userDetails.CoRefname.startsWith("FM") ? "Debit AC: " : "") + (dp['Debit_AC'] ?? ""),
              docDate: "",
              description: desc,
              jobNo: this.userDetails.CoRefname.startsWith("FM") ? dp['Lorry No'] : "",
              taxCode: dp['GSTTaxCode'] ?? "",
              amtExclTax: dp['AmtExclTax'] ? this.formatNumber(dp['AmtExclTax']) : '0.00',
              taxAmt: dp['AmountGST2'] ? this.formatNumber(dp['AmountGST2']) : '0.00',
              amtInclTax: dp['Amount'] ? this.formatNumber(dp['Amount']) : '0.00',
            }
            break;
        }
        allDP.push(detail);
      }
      return allDP;
    },

    formatNumber: function(num) {
      if (Number(num) < 0) {
        let formattedVal = `(${this.$currencyFormat(Number(num) * -1)})`
        return formattedVal;
      }
      return this.$currencyFormat(num);
    },

    designerV3ShareClicked: function(shareLink) {
      if (this.designerV3ReportData[0].email) {
        this.emailPVPopupToMails = this.designerV3ReportData[0].email.split(";").map((email) => ({
          email: email.trim(),
          valid: true,
        }));
      } else {
        this.emailPVPopupToMails = [];
      }
      this.emailPVPopupCCMails = [];
      this.emailPVPopupBCCMails = [];

      if (this.designerV3ReportData.length > 1) {
        this.emailPVPopupSubjectInputValue = 'Payment Vouchers';
        this.emailPVPopupMessageText = `Payment Voucher: ${shareLink}\n\n`;

        for (const pvData of this.designerV3ReportData) {
          this.emailPVPopupMessageText += `Payment Voucher ${pvData['Payment No']}\n`;
          const pvDocRoute = this.$router.resolve(`/GetDocInv/${this.userDetails.LoginUserId}/${this.userDetails.ConfigCode}/${this.designerV3ReportData[0]['Payment No']}`);
          this.emailPVPopupMessageText += `Documents: ${window.location.origin}${pvDocRoute.href}\n`;
        }
      } else {
        this.emailPVPopupSubjectInputValue = `Payment Voucher ${this.designerV3ReportData[0]['Payment No']}`;
        this.emailPVPopupMessageText = `Payment Voucher: ${shareLink}\n`;

        const pvDocRoute = this.$router.resolve(`/GetDocInv/${this.userDetails.LoginUserId}/${this.userDetails.ConfigCode}/${this.designerV3ReportData[0]['Payment No']}`);
        this.emailPVPopupMessageText += `Documents: ${window.location.origin}${pvDocRoute.href}\n`;
      }

      this.showEmailPVPopup = true;
    },

    designerV3AfterPrint: async function() {
      const requestBody = {
        report: "pv",
        pvarray: this.designerV3ReportData.map((reportData) => reportData['Payment No']).join(",")
      }

      const response = await this.$apiCallV2("/PrintPaymentVoucher/UpdatePrintCount", requestBody);
      if (response !== "") {
        this.$showAlert("error", "Fail to update print count", response?.toString() ?? "An error occured when trying to update print count", 
          [{text: "OK"}]
        );
      }
    },

    emailPVPopupButtonClicked: async function(buttonText) {
      if (buttonText === "Cancel") {
        this.showEmailPVPopup = false;
        return;
      }

      if (this.emailPVPopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert("error", "No Valid Emails", "Please enter at least one valid 'To' email", [{text: "OK"}]);
        return;
      }

      if (!this.emailPVPopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert("warning", "No Subject", "No subject was entered\nProceed to email without a subject?",
          [{text: "Proceed"}, {text: "Cancel", color: "lightgray"}]
        );

        if (proceedWithoutSubject === "Cancel") return;
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'PAYMENT',
            docno: this.designerV3ReportData.map((pvDate) => pvData.paymentno).join(","),
            docnofieldname: "",
            recipient: this.emailPVPopupToMails.map((mail) => mail.email).join(";"),
            cc: this.emailPVPopupCCMails.map((mail) => mail.email).join(";"),
            bcc: this.emailPVPopupBCCMails.map((mail) => mail.email).join(";"),
            subject: this.emailPVPopupSubjectInputValue.trim(),
            message: this.emailPVPopupMessageText,
          }
        ]
      };

      this.$fullscreenLoading("Sending Email");
      const sendEmailResponse = await this.$apiCallV2("/PrintPaymentVoucher/SendEmail", requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.("queue to send.")) {
        this.$showAlert("success", "Email successfully sent", "Your email has been successfully sent", [{text: "OK"}]);
        this.showEmailPVPopup = false;
      } else {
        this.$showAlert("error", "Email not sent", "Your email could not be sent. Please try again.", [{text: "OK"}]);
      }
    }
  },
  async mounted() {
    const currentDate = new Date();
    let startDate = new Date(currentDate);
    startDate.setDate(-180);
    startDate = startDate.formatString("yyyy-MM-dd");
    const endDate = currentDate.formatString("yyyy-MM-dd");
    const payListPost = {
      'startdate': startDate,
      'enddate': endDate,
    }

    let mountedPromises = [];
    mountedPromises.push(
      this.$apiCallV2('/PrintPaymentVoucher/GetPaymentListing', payListPost).then((result) => {
        this.paymentNoList = result;
        this.mountedPromisesFulfilledCount++;
      })
    );

    // mountedPromises.push(
    //   this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=PAPERSIZE_PV').then((result) => {
    //     const indicator = result[0].SettingsValue ?? "";
    //     this.pageFormatOption = ['H', 'F'].includes(indicator) ? true : false;
    //     this.pageFormatCaption = indicator == 'H' ? "Full Page Format" : (indicator == 'F' ? 'Half Page Format' : "");
    //     this.updateMountedLoaderMessage(mountedPromises.length);
    //   })
    // );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=LetterHead_PV').then((result) => {
        this.showLetterHeadChecked = result[0].SettingsValue.toLowerCase() != 'false';
        this.updateMountedLoaderMessage(mountedPromises.length);
      })
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=Sign_PV').then((result) => {
        this.chequeSign = result[0].SettingsValue.toLowerCase() == 'true';
        this.updateMountedLoaderMessage(mountedPromises.length);
      })
    );

    mountedPromises.push(
      this.$apiCallV2("/Login/GetDetails").then((result) => {
        this.userDetails = result;
        this.updateMountedLoaderMessage(mountedPromises.length);
      })
    );

    mountedPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'PARTY' }).then((result) => {
        this.payeeTypeList = result;
        this.mountedPromisesFulfilledCount++;
      })
    );

    this.updateMountedLoaderMessage(mountedPromises.length);
    await Promise.allSettled(mountedPromises);
    this.$fullscreenLoading();

    this.selectedCurrencyFormat = this.currencyFormatItems.find((c) => c['APIValue'] == 'original');
    if (this.paymentNoList.length > 0 && this.paymentNoList[0]['Payment No'] == 'string') {
      this.paymentNoList.splice(0,1);
    }

    if (this.userDetails.CoRefname == 'FMH' || this.userDetails.CoRefname == 'DSM') this.groupByDebitAccOption = true;
    if (this.userDetails.CoRefname == 'FTS') this.verifyByField = true;

    if (this.verifyByField) { // reduce api calls if verify by field not required
      this.$fullscreenLoading("Getting Employee List");
      this.verifyByList = await this.$apiCallV2('/Master/GetListMaster', { masterfile: 'EMPLOYEE' });
      this.$fullscreenLoading();
    }

    if (this.$route.query.payno) {
      this.filterByPayNo = true;
      
      const queryParamPayNo = this.$route.query.payno.trim();
      const matchingPayment = this.paymentNoList.find((p) => p['Payment No'] === queryParamPayNo);

      if (matchingPayment) {
        this.selectedStartPaymentNo = matchingPayment;
        this.selectedEndPaymentNo = matchingPayment;
      } else {
        this.startPayNoSearch = queryParamPayNo;
        this.endPayNoSearch = queryParamPayNo;
      }

      this.filterByPaymentDate = false;
    } else {
      this.filterByPaymentDate = true;
      this.paymentStartDate = new Date();
      this.paymentEndDate = new Date();
    }

    this.updatePVFilterSummary();
    await this.getFilteredPVList();
  },
  watch: {
    selectedPayeeType: async function(val) {
      if (val) {
        this.$fullscreenLoading("Getting Payee Code");
        this.payeeCodeList = await this.$apiCallV2('/Master/GetDetailsMaster', { masterfile: 'payeecode', recordid: val['Table'] });
        if (this.payeeCodeList.length > 0) {
          const codeKeys = Object.keys(this.payeeCodeList[0]);
          this.payeeCodeKey = codeKeys[0];
          this.payeeCodeDescKey = codeKeys[1];
        }
        this.$fullscreenLoading();
      }
    },
  }
}
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.print-pv-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .pv-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .pv-filters-and-table-box-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-bottom: 5px;

      > .pv-filter-summary-container {
        margin-right: 5px;
        align-content: center;
        text-align: end;
      }

      .button-filter-content-container {
        text-align: left;
        cursor: auto;

        .pv-filter-container {
          margin-top: 15px;
        }

        .payee-filter-container {
          margin-top: 15px;
        }

        .refresh-button-container {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
      }

      > .select-deselect-all-button {
        margin-left: 5px;
      }
    }

    > .pv-tablebox-container {
      height: calc(100% - 345px);
    }

    > .header-formatting-controls {
      margin-top: 5px;
      width: 50%;
    }

    > .call-to-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }
  }

  > .pv-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }

  > .email-pv-popup {
    .email-pv-popup-content-container {
      display: flex;
      flex-direction: row;
      
      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}

</style>